// frontend/src/homepage/howDebug.js
//
// DEVELOPMENT-ONLY hooks for the pinned "how it works" section.
//
// The pinned stages are driven by scroll, and the two things this project
// verifies pages with cannot scroll: headless Chrome under a virtual-time budget
// (IntersectionObserver never fires, a programmatic scroll never repaints) and
// jsdom (no layout at all). So the section honours `window.__forceHow = p`,
// the prototype's own dev lever, and the page shell turns
// `?hl_debug=how:<p>` into that plus a class that hides everything above the
// section - which puts the pinned stage at the top of a capture.
//
// `?hl_smoke=<look>` picks one of the smoke engine's LOOKS (smokePlume.js) so
// the candidates can be captured and compared side by side; the engine
// validates the name and falls back to its default.
//
// EVERY EXPORT HERE IS INERT IN PRODUCTION: each one checks NODE_ENV first, so a
// production bundle cannot be steered by a query string or a global. The
// module is tiny on purpose; it is the only place that knowledge lives.

/** The class the debug handler puts on <html> to hide the sections above. */
export const HOW_DEBUG_CLASS = "hl-debug-how";

const DEBUG_PARAM = "hl_debug";
const SMOKE_PARAM = "hl_smoke";

const isDev = () => process.env.NODE_ENV !== "production";

/**
 * The forced progress, if a developer set one.
 *
 * @returns {number|null} 0..1, or null when not forced (always null in prod).
 */
export function getForcedHowProgress() {
  if (!isDev() || typeof window === "undefined") return null;
  const value = window.__forceHow;
  if (typeof value !== "number" || Number.isNaN(value)) return null;
  return Math.max(0, Math.min(1, value));
}

/**
 * Parse `?hl_debug=how:<p>` out of a search string.
 *
 * @param {string} search `window.location.search`.
 * @returns {number|null}
 */
export function parseHowDebug(search) {
  if (!isDev() || typeof search !== "string" || !search) return null;
  let params;
  try {
    params = new URLSearchParams(search);
  } catch {
    return null;
  }
  const raw = params.get(DEBUG_PARAM);
  if (!raw || !raw.startsWith("how:")) return null;
  const p = Number(raw.slice(4));
  if (Number.isNaN(p)) return null;
  return Math.max(0, Math.min(1, p));
}

/**
 * Apply the debug state for the page's lifetime; returns the undo.
 *
 * @param {number|null} forced
 * @returns {() => void}
 */
export function applyHowDebug(forced) {
  if (!isDev() || forced === null || typeof window === "undefined") return () => {};
  window.__forceHow = forced;
  document.documentElement.classList.add(HOW_DEBUG_CLASS);
  return () => {
    delete window.__forceHow;
    document.documentElement.classList.remove(HOW_DEBUG_CLASS);
  };
}

/**
 * Parse `?hl_smoke=<look>` out of a search string.
 *
 * @param {string} search `window.location.search`.
 * @returns {string|null} the look name as typed (the engine validates it).
 */
export function parseSmokeLook(search) {
  if (!isDev() || typeof search !== "string" || !search) return null;
  let params;
  try {
    params = new URLSearchParams(search);
  } catch {
    return null;
  }
  const raw = params.get(SMOKE_PARAM);
  return raw && /^[a-z0-9_-]{1,16}$/i.test(raw) ? raw : null;
}

/**
 * Publish the chosen smoke look for the page's lifetime; returns the undo.
 *
 * @param {string|null} look
 * @returns {() => void}
 */
export function applySmokeLook(look) {
  if (!isDev() || !look || typeof window === "undefined") return () => {};
  window.__hlSmokeLook = look;
  return () => {
    delete window.__hlSmokeLook;
  };
}

/**
 * The smoke look a developer chose, if any.
 *
 * @returns {string|null} always null in prod.
 */
export function getSmokeLook() {
  if (!isDev() || typeof window === "undefined") return null;
  const value = window.__hlSmokeLook;
  return typeof value === "string" && value ? value : null;
}
